import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search as SearchIcon, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import ArticleCard from "@/components/public/ArticleCard";
import { Reveal } from "@/components/public/Reveal";
import { BrushUnderline } from "@/components/public/Brush";

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [input, setInput] = useState(q);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useSeo({ title: `Search | Lucrative Laptop`, description: "Search stories, ideas and tools on Lucrative Laptop." });

  useEffect(() => {
    if (!q) { setResults([]); return; }
    setLoading(true);
    api.get("/public/search", { params: { q } }).then((r) => setResults(r.data)).finally(() => setLoading(false));
  }, [q]);

  const submit = (e) => { e.preventDefault(); setParams(input.trim() ? { q: input.trim() } : {}); };

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 pt-10 pb-12 min-h-[70vh]">
      <div className="flex items-center gap-3 mb-3"><span className="w-6 h-0.5 bg-brand" /><span className="label-mono">Search</span></div>
      <h1 className="font-poster uppercase text-white leading-[0.9] text-5xl lg:text-7xl mb-10">
        FIND WHAT <span className="relative inline-block text-brand">YOU NEED.<BrushUnderline className="absolute left-0 -bottom-2 w-full h-4 text-brand" /></span>
      </h1>

      <form onSubmit={submit} className="mb-12">
        <div className="flex items-center gap-3 bg-ink-900 border-2 border-ink-600 px-5 py-4 max-w-2xl focus-within:border-brand transition-colors">
          <SearchIcon size={20} className="text-brand" />
          <input autoFocus data-testid="search-page-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="SEARCH ARTICLES, TOPICS, TAGS" className="flex-1 bg-transparent outline-none text-white placeholder:text-white/40 font-cond uppercase tracking-wider" />
          <button type="submit" data-testid="search-page-submit" className="btn-poster py-2 px-5 text-sm"><span>GO</span><ArrowRight size={15} className="relative z-10" /></button>
        </div>
      </form>

      {loading ? (
        <div className="text-brand font-mono-label">searching...</div>
      ) : q && results.length === 0 ? (
        <div className="text-white/60 py-16 font-hand text-3xl" data-testid="search-empty">Nothing for "{q}". Try another word.</div>
      ) : (
        <>
          {q && <p className="font-cond uppercase tracking-wider text-sm text-white/50 mb-6">{results.length} result{results.length !== 1 ? "s" : ""} for "{q}"</p>}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="search-results">
            {results.map((p, i) => <Reveal key={p.slug} delay={(i % 3) * 0.05}><ArticleCard post={p} variant="default" /></Reveal>)}
          </div>
        </>
      )}
    </div>
  );
}
